// Lógica pura de Cohorts: cómo se dice una regla, el borrador del armador y los mensajes de error.
// Nada de aquí toca la red ni React.
import { ApiError, userMessageOf } from '@/api/errors';
import {
    COHORT_ERROR_CODES,
    type CohortErrorCode,
    type CohortFactType,
    type CohortFactValue,
    type CohortOperator,
} from '@/api/services/loan-offerings/cohorts.contract';
import {
    COHORT_DESCRIPTION_MAX_LENGTH,
    COHORT_NAME_MAX_LENGTH,
    MAX_COHORT_CONDITIONS,
    NOT_COUNTED_LABEL,
    type Cohort,
    type CohortCondition,
    type CohortConditionResult,
    type CohortDraft,
    type CohortField,
    type CohortListFilter,
    type CohortSegmentRef,
    type CohortStatus,
} from './types';

const collator = new Intl.Collator('es-MX', { sensitivity: 'base', numeric: true });
const countFormat = new Intl.NumberFormat('es-MX');
const pctFormat = new Intl.NumberFormat('es-MX', { maximumFractionDigits: 1 });

// ---------------------------------------------------------------------------------------------
// Operadores
// ---------------------------------------------------------------------------------------------

const NUMBER_OPERATOR_LABEL: Record<CohortOperator, string> = {
    EQUALS: '=',
    NOT_EQUALS: '≠',
    GT: '>',
    GTE: '≥',
    LT: '<',
    LTE: '≤',
    IN: 'es alguno de',
    NOT_IN: 'no es ninguno de',
};

const CHOICE_OPERATOR_LABEL: Record<CohortOperator, string> = {
    ...NUMBER_OPERATOR_LABEL,
    EQUALS: 'es',
    NOT_EQUALS: 'no es',
};

/** Cómo se lee el operador según el tipo del dato: símbolos en números, palabras en opciones. */
export function operatorLabel(op: CohortOperator, type: CohortFactType): string {
    return type === 'NUMBER' ? NUMBER_OPERATOR_LABEL[op] : CHOICE_OPERATOR_LABEL[op];
}

/** «es alguno de» y «no es ninguno de» comparan contra una lista. */
export function isListOperator(op: CohortOperator): boolean {
    return op === 'IN' || op === 'NOT_IN';
}

/** Por `.find`: indexando, una llave como `constructor` acertaría contra el prototipo. */
export function findField(catalog: readonly CohortField[], key: string): CohortField | undefined {
    return catalog.find((field) => field.key === key);
}

// ---------------------------------------------------------------------------------------------
// La regla, dicha en palabras
// ---------------------------------------------------------------------------------------------

export function formatCount(count: number): string {
    return countFormat.format(count);
}

export function formatPct(pct: number): string {
    return `${pctFormat.format(pct)} %`;
}

/** Un valor del cliente o de la regla: número con unidad, la etiqueta de la opción, o sí/no. */
export function formatFactValue(value: CohortFactValue, field: CohortField | undefined): string {
    if (typeof value === 'boolean') return value ? 'sí' : 'no';
    if (typeof value === 'number') {
        const unit = field?.unit;
        return unit ? `${formatCount(value)} ${unit}` : formatCount(value);
    }
    return field?.options.find((option) => option.value === value)?.label ?? value;
}

/** Lo que falta en una condición incompleta. */
const MISSING_VALUE = '…';

/** Una condición en palabras: «Créditos liquidados ≥ 3 créditos», «Nivel SCI es Oro». */
export function conditionPhrase(
    condition: CohortCondition,
    catalog: readonly CohortField[],
): string {
    const field = findField(catalog, condition.field);
    const label = field?.label ?? condition.field;
    const type: CohortFactType =
        field?.type ?? (typeof condition.value === 'number' ? 'NUMBER' : 'SELECT');

    if (type === 'BOOLEAN') {
        const value = condition.value;
        return `${label}: ${typeof value === 'boolean' ? formatFactValue(value, field) : MISSING_VALUE}`;
    }

    const op = operatorLabel(condition.op, type);
    if (isListOperator(condition.op)) {
        const values = condition.values.map((value) => formatFactValue(value, field));
        return `${label} ${op} ${values.length === 0 ? MISSING_VALUE : values.join(', ')}`;
    }
    const value = condition.value;
    return `${label} ${op} ${value === null ? MISSING_VALUE : formatFactValue(value, field)}`;
}

/** La regla entera para la tabla: las condiciones unidas con «y». */
export function cohortSentence(
    conditions: readonly CohortCondition[],
    catalog: readonly CohortField[],
): string {
    return conditions.map((condition) => conditionPhrase(condition, catalog)).join(' y ');
}

/** «Qué define este cohort»: a quién alcanza lo que está en el armador. */
export function definitionSentence(
    conditions: readonly CohortCondition[],
    catalog: readonly CohortField[],
): string {
    if (conditions.length === 0) return 'Agrega al menos una condición.';
    const rule = cohortSentence(conditions, catalog);
    return conditions.length === 1
        ? `Entra el cliente que cumple: ${rule}.`
        : `Entra el cliente que cumple todas: ${rule}.`;
}

/** La columna Clientes: «n · x %», o «Sin calcular». */
export function cohortCountText(cohort: Pick<Cohort, 'memberCount' | 'pctTotal'>): string {
    if (cohort.memberCount === null) return NOT_COUNTED_LABEL;
    const count = formatCount(cohort.memberCount);
    return cohort.pctTotal === null ? count : `${count} · ${formatPct(cohort.pctTotal)}`;
}

// ---------------------------------------------------------------------------------------------
// Lista
// ---------------------------------------------------------------------------------------------

export function filterCohorts(
    cohorts: readonly Cohort[],
    query: string,
    status: CohortListFilter,
): Cohort[] {
    const needle = query.trim().toLowerCase();
    return cohorts.filter(
        (cohort) =>
            cohort.status === status &&
            (needle === '' ||
                cohort.name.toLowerCase().includes(needle) ||
                cohort.description.toLowerCase().includes(needle)),
    );
}

export function sortCohorts(cohorts: readonly Cohort[]): Cohort[] {
    return [...cohorts].sort((a, b) => collator.compare(a.name, b.name));
}

export function countByStatus(cohorts: readonly Cohort[]): Record<CohortListFilter, number> {
    const active = cohorts.filter((cohort) => cohort.status === 'ACTIVE').length;
    return { ACTIVE: active, ARCHIVED: cohorts.length - active };
}

// ---------------------------------------------------------------------------------------------
// Borrador del armador
// ---------------------------------------------------------------------------------------------

function copyCondition(condition: CohortCondition): CohortCondition {
    return { ...condition, values: [...condition.values] };
}

export function draftFromCohort(cohort: Cohort): CohortDraft {
    return {
        name: cohort.name,
        description: cohort.description,
        conditions: cohort.conditions.map(copyCondition),
    };
}

/** El valor con el que arranca un dato: la primera opción, «sí», o vacío para que se escriba. */
function initialValue(field: CohortField): CohortFactValue | null {
    if (field.type === 'SELECT') return field.options.at(0)?.value ?? null;
    if (field.type === 'BOOLEAN') return true;
    return null;
}

/** Una condición nueva sobre ese dato, con su primer operador. */
export function conditionForField(field: CohortField): CohortCondition {
    const op = field.operators.at(0) ?? 'EQUALS';
    return {
        field: field.key,
        op,
        value: isListOperator(op) ? null : initialValue(field),
        values: [],
    };
}

/** La condición con la que arranca un cohort o un «+ Agregar condición»: el primer dato con fuente. */
export function defaultCondition(catalog: readonly CohortField[]): CohortCondition | null {
    const field = catalog.find((item) => item.hasSource);
    return field ? conditionForField(field) : null;
}

export function newCohortDraft(
    name: string,
    description: string,
    catalog: readonly CohortField[],
): CohortDraft {
    const first = defaultCondition(catalog);
    return { name, description, conditions: first ? [first] : [] };
}

/** Cambiar de operador conserva el valor cuando se puede: el único valor pasa a la lista y al revés. */
export function changeConditionOperator(
    condition: CohortCondition,
    op: CohortOperator,
): CohortCondition {
    const fromList = isListOperator(condition.op);
    const toList = isListOperator(op);
    if (fromList === toList) return { ...copyCondition(condition), op };
    if (toList) {
        const values = typeof condition.value === 'string' ? [condition.value] : [];
        return { field: condition.field, op, value: null, values };
    }
    return { field: condition.field, op, value: condition.values.at(0) ?? null, values: [] };
}

function sameValues(a: readonly string[], b: readonly string[]): boolean {
    if (a.length !== b.length) return false;
    const right = [...b].sort();
    return [...a].sort().every((value, index) => value === right[index]);
}

/** Mismas condiciones en el mismo orden. Reordenar la lista de opciones no cuenta. */
export function sameConditions(
    a: readonly CohortCondition[],
    b: readonly CohortCondition[],
): boolean {
    if (a.length !== b.length) return false;
    return a.every((left, index) => {
        const right = b[index];
        return (
            left.field === right.field &&
            left.op === right.op &&
            left.value === right.value &&
            sameValues(left.values, right.values)
        );
    });
}

/** Lo mismo guardado, sin contar espacios al borde: así compara el editor contra el servidor. */
export function sameCohortContent(a: CohortDraft, b: CohortDraft): boolean {
    return (
        a.name.trim() === b.name.trim() &&
        a.description.trim() === b.description.trim() &&
        sameConditions(a.conditions, b.conditions)
    );
}

/** Los campos que cambiaron: nombre, descripción y condiciones. */
export function countDraftChanges(original: CohortDraft, draft: CohortDraft): number {
    let changes = 0;
    if (original.name !== draft.name) changes += 1;
    if (original.description !== draft.description) changes += 1;
    if (!sameConditions(original.conditions, draft.conditions)) changes += 1;
    return changes;
}

// ---------------------------------------------------------------------------------------------
// Validación
// ---------------------------------------------------------------------------------------------

/** Qué le falta a una condición para poder guardarse, o `null` si está completa. */
export function conditionProblem(
    condition: CohortCondition,
    field: CohortField | undefined,
): string | null {
    if (!field) return `«${condition.field}» ya no está en el catálogo. Quita esa condición.`;
    if (!field.hasSource) {
        return `${field.label} no tiene fuente: ${field.missingSourceReason ?? 'ningún sistema produce ese dato.'}`;
    }
    if (!field.operators.includes(condition.op)) return `Elige un operador para ${field.label}.`;
    if (isListOperator(condition.op)) {
        return condition.values.length === 0
            ? `Elige al menos una opción para ${field.label}.`
            : null;
    }
    const value = condition.value;
    switch (field.type) {
        case 'NUMBER':
            if (typeof value !== 'number' || !Number.isFinite(value)) {
                return `Escribe el valor de ${field.label}.`;
            }
            // Los datos del cliente son conteos: días y créditos no tienen negativos ni decimales.
            return Number.isInteger(value) && value >= 0
                ? null
                : `${field.label} va en números enteros desde 0.`;
        case 'SELECT':
            return typeof value === 'string' && field.options.some((o) => o.value === value)
                ? null
                : `Elige el valor de ${field.label}.`;
        case 'BOOLEAN':
            return typeof value === 'boolean' ? null : `Elige sí o no para ${field.label}.`;
    }
}

/** Lo que impide evaluar las condiciones: sirve igual para guardar y para probar con un cliente. */
export function conditionsProblems(
    conditions: readonly CohortCondition[],
    catalog: readonly CohortField[],
): string[] {
    if (conditions.length === 0) return ['Agrega al menos una condición.'];
    if (conditions.length > MAX_COHORT_CONDITIONS) {
        return [`Un cohort admite hasta ${String(MAX_COHORT_CONDITIONS)} condiciones.`];
    }
    return conditions.flatMap((condition) => {
        const problem = conditionProblem(condition, findField(catalog, condition.field));
        return problem === null ? [] : [problem];
    });
}

/**
 * Lo que la pantalla corta antes de que el backend responda 400 o 422. Con `original`, las
 * condiciones se validan solo si cambiaron: un dato que perdió su fuente no frena un cambio de nombre.
 */
export function validateDraft(
    draft: CohortDraft,
    catalog: readonly CohortField[],
    original?: CohortDraft,
): string[] {
    const problems: string[] = [];
    const name = draft.name.trim();
    if (name === '') problems.push('Ponle un nombre al cohort.');
    else if (name.length > COHORT_NAME_MAX_LENGTH) {
        problems.push(`El nombre no puede pasar de ${String(COHORT_NAME_MAX_LENGTH)} caracteres.`);
    }
    if (draft.description.length > COHORT_DESCRIPTION_MAX_LENGTH) {
        problems.push(
            `La descripción no puede pasar de ${String(COHORT_DESCRIPTION_MAX_LENGTH)} caracteres.`,
        );
    }
    if (original !== undefined && sameConditions(original.conditions, draft.conditions)) {
        return problems;
    }
    return [...problems, ...conditionsProblems(draft.conditions, catalog)];
}

// ---------------------------------------------------------------------------------------------
// Probar con un cliente
// ---------------------------------------------------------------------------------------------

/** El valor real del cliente, o que no lo tiene. */
export function actualText(result: CohortConditionResult, catalog: readonly CohortField[]): string {
    if (result.actual === null) return 'no tiene el dato';
    return `tiene ${formatFactValue(result.actual, findField(catalog, result.condition.field))}`;
}

// ---------------------------------------------------------------------------------------------
// Errores
// ---------------------------------------------------------------------------------------------

export const EVALUATION_UNAVAILABLE_MESSAGE =
    'No se pudo evaluar ahora; intenta de nuevo en un momento.';

/** Cada `code` del contrato con su mensaje y lo que el operador tiene que hacer. */
const MESSAGE_BY_COHORT_CODE: Record<CohortErrorCode, string> = {
    COHORT_NOT_FOUND: 'Ese cohort ya no existe. Refresca la lista y vuelve a intentarlo.',
    COHORT_VERSION_CONFLICT:
        'Alguien más cambió este cohort mientras lo editabas. Recarga para ver la versión actual y vuelve a aplicar tus cambios.',
    COHORT_NAME_TAKEN: 'Ya hay otro cohort activo con ese nombre. Ponle uno distinto.',
    COHORT_IN_USE: 'Lo usan segmentos activos. Quítalo de esos segmentos antes de archivarlo.',
    COHORT_RULE_INVALID:
        'Una de las condiciones no es válida. Revisa el dato, el operador y el valor de cada una.',
    COHORT_FIELD_WITHOUT_SOURCE:
        'Una condición usa un dato que ningún sistema produce todavía. Quítala para guardar.',
    COHORT_EVALUATOR_UNAVAILABLE: EVALUATION_UNAVAILABLE_MESSAGE,
    LOAN_CREDIT_UNAVAILABLE: EVALUATION_UNAVAILABLE_MESSAGE,
};

function isCohortErrorCode(code: string): code is CohortErrorCode {
    return (COHORT_ERROR_CODES as readonly string[]).includes(code);
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

/** El mensaje que se le enseña al operador. Nunca el `message` crudo de un error de programación. */
export function cohortErrorCopy(error: unknown): string {
    if (error instanceof ApiError && isCohortErrorCode(error.code)) {
        return MESSAGE_BY_COHORT_CODE[error.code];
    }
    return userMessageOf(error);
}

/** Probar con un cliente: cualquier 503 es que el motor o loan-credit no respondieron. */
export function testErrorCopy(error: unknown): string {
    if (error instanceof ApiError && error.httpStatus === 503)
        return EVALUATION_UNAVAILABLE_MESSAGE;
    return cohortErrorCopy(error);
}

/** Archivar o reactivar: al reactivar, el nombre repetido se arregla renombrando primero. */
export function statusErrorCopy(error: unknown, to: CohortStatus): string {
    if (to === 'ACTIVE' && error instanceof ApiError && error.code === 'COHORT_NAME_TAKEN') {
        return 'Otro cohort activo ya usa este nombre. Renómbralo, guarda y vuelve a reactivarlo.';
    }
    return cohortErrorCopy(error);
}

const CONDITION_PATH = /^conditions\[(\d+)\]/;

const WITHOUT_SOURCE_PROBLEM = 'Este dato no tiene fuente. Quita la condición para guardar.';
const REJECTED_PROBLEM =
    'El servidor rechazó esta condición. Revisa el dato, el operador y el valor.';

/**
 * Qué condición marcar tras un 422: por posición, con lo que hay que hacer. Sale del `data` de
 * `COHORT_FIELD_WITHOUT_SOURCE` (`fields[].index`) y de `COHORT_RULE_INVALID` (`conditions[i]…`).
 */
export function conditionErrorsOf(error: unknown): Map<number, string> {
    const byIndex = new Map<number, string>();
    if (!(error instanceof ApiError) || !isRecord(error.data)) return byIndex;
    const data = error.data;
    if (error.code === 'COHORT_FIELD_WITHOUT_SOURCE' && Array.isArray(data.fields)) {
        for (const item of data.fields as unknown[]) {
            if (isRecord(item) && typeof item.index === 'number') {
                byIndex.set(item.index, WITHOUT_SOURCE_PROBLEM);
            }
        }
    }
    if (error.code === 'COHORT_RULE_INVALID' && Array.isArray(data.problems)) {
        for (const problem of data.problems as unknown[]) {
            const match = typeof problem === 'string' ? CONDITION_PATH.exec(problem) : null;
            if (match) byIndex.set(Number(match[1]), REJECTED_PROBLEM);
        }
    }
    return byIndex;
}

/** Los segmentos que trae un 409 `COHORT_IN_USE`, o `null` si el error es otro. */
export function cohortInUseSegments(error: unknown): CohortSegmentRef[] | null {
    if (!(error instanceof ApiError) || error.code !== 'COHORT_IN_USE') return null;
    const data = error.data;
    if (!isRecord(data) || !Array.isArray(data.segments)) return [];
    return data.segments.flatMap((item: unknown) =>
        isRecord(item) && typeof item.segmentId === 'string' && typeof item.name === 'string'
            ? [{ segmentId: item.segmentId, name: item.name }]
            : [],
    );
}
